import { getCookie } from '@olxeu-a2b/core/src/utils/cookie';
import {
  KycResultResponse,
  KycRetryRequest,
  KycStatusResponse,
  OrderPreviewPriceResponseV4,
  PostOfferMarketPayRequest,
  PostOfferResponse,
  PostOrderRequest,
  PostOrderResponse,
  PostReusableDataRequest,
  PostReusableDataResponse,
  PutOfferResponse,
  UploadAttachmentBinary,
  UploadAttachmentResponse,
} from '@olxeu-a2b/core/typings/components';

import {
  acceptOrderPath,
  deleteOfferParams,
  getOfferParams,
  getOrderPreviewPricev4Params,
  getSellerStatsPath,
  getTransactionPath,
  getOfferByAdIdAliasPath,
  postAskForDeliveryParams,
  putOfferParams,
  rejectSafedealOrderV2Params,
  getKycRegistrationNeededHeader,
  getKycStateForConfirmationHeader,
  postKycForTier1Header,
  getUserInvoicingInformationHeader,
  uploadAttachmentParams,
} from '@olxeu-a2b/core/typings/DeliverySDK';

import { KYC_STATUS } from '../constants';
import { parseTransaction, withTimeout } from '../extras';
import useSDK, { UseSDKHook } from './useSDK';

export const useGetCheckPsp = () =>
  useSDK((sdk, headers?: getKycStateForConfirmationHeader) =>
    sdk.getKycStateForConfirmation(undefined, headers),
  );

export const usePostKycTierOne = () =>
  useSDK(
    (
      sdk,
      params?: {
        headers: postKycForTier1Header;
        data: { iban: string; birthDate?: string | null };
      },
    ) => sdk.postKycForTier1({ data: params.data }, params.headers),
  );

export const usePostKycTierOneAndTwo = () =>
  useSDK(
    (
      sdk,
      params?: {
        headers: postKycForTier1Header;
        data: { iban: string; birthDate?: string | null };
      },
    ) => sdk.cacheKycForTier1AndReturnTier2Url({ data: params.data }, params.headers),
  );

export const useGetKycRegistrationNeeded = () =>
  useSDK((sdk, headers: getKycRegistrationNeededHeader) =>
    sdk.getKycRegistrationNeeded(undefined, headers),
  );

export const useGetOffer = () => useSDK((sdk, offerId: getOfferParams) => sdk.getOffer(offerId));

export const usePutOffer = (): UseSDKHook<putOfferParams, PutOfferResponse> =>
  useSDK((sdk, params: putOfferParams) => sdk.putOffer(params));

export const useDeleteOffer = (): UseSDKHook<deleteOfferParams, { success: true }> =>
  useSDK((sdk, params: deleteOfferParams) =>
    sdk.deleteOffer(params).then(() => ({ success: true })),
  );

export const useGetOfferDeadlines = (): UseSDKHook<
  string,
  {
    confirmOrderDeadline: number;
    sendItemDeadline: number;
  }
> =>
  useSDK((sdk, offerId: string) =>
    sdk.getOfferDeadlines({ offerId }).then(({ data }) => {
      const [confirmOrder, sendItem] = data;

      return {
        confirmOrderDeadline: confirmOrder.attributes.days,
        sendItemDeadline: sendItem.attributes.days,
      };
    }),
  );

export const useGetOrderPreviewPriceV4 = (): UseSDKHook<
  getOrderPreviewPricev4Params,
  OrderPreviewPriceResponseV4['data']
> =>
  useSDK((sdk, params: getOrderPreviewPricev4Params) =>
    sdk.getOrderPreviewPricev4(params).then(({ data }) => data),
  );

export const useGetTransaction = (orderId: string) =>
  useSDK(sdk => {
    const getTransaction = withTimeout((params: getTransactionPath) => sdk.getTransaction(params));
    return getTransaction({
      orderId,
    }).then(({ data }) => parseTransaction(data));
  });

export const useGetOfferOption = (adId: string) =>
  useSDK(sdk => {
    const getOfferOption = withTimeout((params: getOfferByAdIdAliasPath) =>
      sdk.getOfferByAdIdAlias(params),
    );
    return getOfferOption({
      adId,
    }).then(({ data }) => data);
  });

export const useRejectOrder = (): UseSDKHook<string, { success: true }> =>
  useSDK((sdk, orderId: string) => {
    const rejectOrder = withTimeout(sdk.rejectOrder.bind(sdk));
    return rejectOrder({ orderId }).then(() => ({ success: true } as const));
  });

export const useCancelTransaction = (): UseSDKHook<string, { success: true }> =>
  useSDK((sdk, orderId: string) => {
    const cancelTransaction = withTimeout(sdk.cancelTransaction.bind(sdk));
    return cancelTransaction({ orderId }).then(() => ({ success: true } as const));
  });

export const usePostMarketPayOffer = (): UseSDKHook<
  PostOfferMarketPayRequest['data']['attributes'],
  PostOfferResponse
> =>
  useSDK((sdk, params: PostOfferMarketPayRequest['data']['attributes']) =>
    sdk.postMarketPayOffer(
      {
        data: { attributes: params, type: 'marketpay-offer' },
      },
      {},
      'marketpay-offer',
    ),
  );

export const usePostMarketPayOrder = (): UseSDKHook<
  PostOrderRequest['data']['attributes'],
  PostOrderResponse
> =>
  useSDK((sdk, params) =>
    sdk.postOrder({
      data: {
        type: 'order',
        attributes: params,
      },
    }),
  );

export const usePostKYCRetry = (): UseSDKHook<
  KycRetryRequest['data']['attributes'],
  KycStatusResponse
> =>
  useSDK((sdk, params) =>
    sdk.postKycRetry(
      {
        data: {
          type: 'kyc-retry',
          attributes: params,
        },
      },
      null,
      'kyc-retry',
    ),
  );

export const useGetKYC = (() => {
  let memoResult: KycResultResponse | null = null;

  return () =>
    useSDK(sdk => {
      if (!memoResult) {
        return sdk
          .getKyc()
          .then(kycStatus => {
            memoResult = kycStatus;
            return kycStatus;
          })
          .catch(() => {
            memoResult = {
              kycAction: KYC_STATUS.NOT_NEEDED,
            };
            return memoResult;
          });
      }
      return Promise.resolve(memoResult);
    });
})();

export const useGetKYCAccount = () => useSDK(sdk => sdk.getKycAccount());

export const usePutAskForDelivery = (): UseSDKHook<postAskForDeliveryParams, unknown> =>
  useSDK((sdk, params) => sdk.postAskForDelivery(params));

export const useSafeDealAccept = (): UseSDKHook<string, { success: true }> =>
  useSDK((sdk, orderId: string) => {
    const safeDealAccept = withTimeout((params: acceptOrderPath) => sdk.acceptOrder(params));
    return safeDealAccept({ orderId }).then(() => ({ success: true } as const));
  });

export const useSafeDealRejection = (): UseSDKHook<
  {
    orderId: string;
    rejectionData: rejectSafedealOrderV2Params['data']['attributes'];
  },
  { success: true }
> =>
  useSDK((sdk, { orderId, rejectionData }) => {
    const safeDealReject = withTimeout((params: rejectSafedealOrderV2Params) =>
      sdk.rejectSafedealOrderV2(params),
    );
    return safeDealReject({
      orderId,
      ...{
        data: {
          attributes: rejectionData,
        },
      },
    }).then(() => ({ success: true }));
  });

export const useSafeDealUpload = (): UseSDKHook<
  {
    file: File;
    orderId: string;
  },
  {
    data: UploadAttachmentResponse['data']['attributes'];
  }
> =>
  useSDK((sdk, { file, orderId }) => {
    const safeDealUpload = withTimeout((params: UploadAttachmentBinary) =>
      sdk.uploadOrderAttachment({ file, orderId }),
    );
    // type for file from core is invalid
    return safeDealUpload({ file } as unknown as uploadAttachmentParams).then(
      ({ data }) => data.attributes,
    );
  });

export const useGetReusableData = () => useSDK(sdk => sdk.getReusableData());

export const usePostReusableData = (): UseSDKHook<
  PostReusableDataRequest,
  PostReusableDataResponse
> => useSDK((sdk, params) => sdk.postReusableData(params));

export const usePostBillingData = () => useSDK((sdk, params) => sdk.postBillingData(params));

export const useGetSellerStats = () =>
  useSDK((sdk, params: getSellerStatsPath) => sdk.getSellerStats(params));

export const useGetUserInvoicingInformation = (headers?: getUserInvoicingInformationHeader) =>
  useSDK(sdk => sdk.getUserInvoicingInformation(undefined, headers).then(data => data?.data));

export const useGetLatestBillingInfo = () =>
  useSDK(sdk => sdk.getLastBillingData(undefined).then(data => data?.data));

export default {
  usePutAskForDelivery,
  usePostMarketPayOffer,
  usePostMarketPayOrder,
  useGetOfferDeadlines,
  useGetOrderPreviewPriceV4,
  useGetTransaction,
  useRejectOrder,
  useDeleteOffer,
  useGetOffer,
  usePostKYCRetry,
  useGetKYC,
  useSafeDealAccept,
  useSafeDealUpload,
  useGetReusableData,
  usePostReusableData,
  useGetSellerStats,
  useGetUserInvoicingInformation,
};
